import { Field } from 'formik'
import React, { useMemo } from 'react'

import { Checkbox, Fieldset, InputUI, Select } from '@fourthwall/components'
import { useFormikContext } from '@fourthwall/utils/dist/formik'
import { useDiscord } from '@modules/Dashboard/pages/Apps/pages/AppsDiscord'

import { Tag } from '../../../../../components/Tag'
import { FREE_TIER_ID } from '../../../consts'
import { useFreeTierQuery } from '../../../hooks/useFreeTierQuery'
import { useTiersQuery } from '../../../hooks/useTiersQuery'
import type { FormValues } from '../../types'
import { FormFields } from '../../types'
import * as S from './styled'

export const TiersFields = () => {
  const { discord } = useDiscord()
  const { tiersQuery } = useTiersQuery()
  const { freeTierQuery } = useFreeTierQuery()

  const { values, errors, setFieldValue } = useFormikContext<FormValues>()

  const tiers = useMemo(() => {
    if (!freeTierQuery.data) return []
    if (!tiersQuery.data) return []

    if (freeTierQuery.data.status === 'Hidden') {
      return tiersQuery.data.tiers.sort((a, b) => a.position - b.position)
    }

    // Note: Paid and free tier could have the same id, so we need to add a unique id to the free tier
    return [...tiersQuery.data.tiers, { ...freeTierQuery.data, id: FREE_TIER_ID }].sort(
      (a, b) => a.position - b.position,
    )
  }, [freeTierQuery.data, tiersQuery.data])

  const getDiscordRoleSelectOptions = (tierId: number) => {
    if (discord.roles) {
      const filteredRoles = [...discord.roles].filter((role) => {
        if (values.selectedRoles[tierId].indexOf(role.id) === -1) return true

        return false
      })

      return [
        { label: 'Add new role', value: 'Add new role', disabled: true },
        ...filteredRoles.map((role) => ({
          label: role.name,
          value: role.id,
          key: role.id,
        })),
      ]
    }

    return []
  }

  const renderDiscordRoleSelect = (tierId: number) => {
    if (discord.roles && values.type === 'Discord') {
      const fieldName = `${FormFields.selectedRoles}.${tierId}`

      const handleAddRole = (role: string) => {
        setFieldValue(fieldName, [...values.selectedRoles[tierId], role])
      }

      const handleRemoveRole = (role: string) => {
        setFieldValue(
          fieldName,
          values.selectedRoles[tierId].filter((selectedRole) => selectedRole !== role),
        )
      }

      return (
        <S.RolesContainer>
          <Select
            label="Add new role"
            options={getDiscordRoleSelectOptions(tierId)}
            value=""
            disabled={discord.isLoading}
            onValueChange={handleAddRole}
          />
          <S.DiscordRoles>
            {values.selectedRoles[tierId].map((roleId) => {
              const role = discord.roles?.find((role) => role.id === roleId)

              return (
                <Tag
                  label={role?.name ? role.name : 'Role removed from server'}
                  disabled={discord.isLoading}
                  onRemove={() => handleRemoveRole(roleId)}
                />
              )
            })}
          </S.DiscordRoles>
        </S.RolesContainer>
      )
    }
  }

  const renderTiers = () =>
    tiers.map((tier) => {
      const isFreeTierDisabled = values.type !== 'Custom' && tier.id === FREE_TIER_ID

      return (
        <>
          <Field
            component={Checkbox}
            name={`${FormFields.selectedTiers}.${tier.id}`}
            label={tier.name}
            checked={isFreeTierDisabled ? false : values.selectedTiers?.[tier.id]}
            native
            disabled={(values.type === 'Discord' && discord.isLoading) || isFreeTierDisabled}
            tooltip={isFreeTierDisabled ? "This perk can't be added to a free tier." : undefined}
          />
          {values.selectedTiers?.[tier.id] &&
            tier.id !== FREE_TIER_ID &&
            renderDiscordRoleSelect(tier.id)}
        </>
      )
    })

  return (
    <Fieldset.Row>
      <Fieldset.Item>
        <S.TiersLabel>Included in tiers:</S.TiersLabel>
        <S.TiersContainer>{renderTiers()}</S.TiersContainer>
        <S.Errors>
          {errors.selectedTiers && <InputUI.Error>{errors.selectedTiers}</InputUI.Error>}
          {/* @ts-ignore */}
          {errors.selectedRoles && <InputUI.Error>{errors.selectedRoles}</InputUI.Error>}
        </S.Errors>
      </Fieldset.Item>
    </Fieldset.Row>
  )
}
